import { formatDate } from '@angular/common';
import { HttpClient, httpResource, HttpResourceRef } from '@angular/common/http';
import { Injectable, computed, effect, inject, linkedSignal, signal } from '@angular/core';
import { ParamMap, Params } from '@angular/router';
import { VideoHubService } from '@core/services/video-hub.service';
import { PublicationStatus } from '@shared/enums/publication-status.enum';
import {
  CaseDisplay,
  CasePeriod,
  CasePublication,
  CaseSortOrder,
} from '@shared/enums/case-library';
import { CaseRange, CaseSearch, Video } from '@shared/models';
import { environment } from 'src/environments/environment';
import { caseSearchRequest } from './case-search-request';
import { STORAGE_KEYS } from '@shared/constants';
import { loadPreference, savePreference } from '@shared/utils';
import { CASE_CATEGORY_IDS, FAVOURITES_CHIP } from '../case-categories';

/* The same kept categories, in whatever order: asking again for them would be for nothing. */
function sameCategories(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && [...a].sort().join() === [...b].sort().join();
}

function storedDisplay(): CaseDisplay {
  return loadPreference(STORAGE_KEYS.LIBRARY_DISPLAY) === CaseDisplay.List
    ? CaseDisplay.List
    : CaseDisplay.Grid;
}

const NO_RANGE: CaseRange = { start: null, end: null };

/** The reader's local calendar day of `date`, as `YYYY-MM-DD`. */
function day(date: Date): string {
  return formatDate(date, 'yyyy-MM-dd', 'en-US');
}

/* Weeks start on Monday, as the application's calendar does. */
function rangeOf(period: CasePeriod.Week | CasePeriod.Month, today: Date): CaseRange {
  const year = today.getFullYear();
  const month = today.getMonth();
  // A day ≤ 0 rolls back into the previous month.
  const firstDay = period === CasePeriod.Month ? 1 : today.getDate() - ((today.getDay() + 6) % 7);
  return { start: day(new Date(year, month, firstDay)), end: day(today) };
}

/* Null when the text is not a real day: 2026-02-31 written back would not read the same. */
function parseDay(text: string | null): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text ?? '');
  if (!match) {
    return null;
  }
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return day(date) === text ? text : null;
}

function known<T extends string>(values: readonly T[], text: string | null, fallback: T): T {
  return values.find((value) => value === text) ?? fallback;
}

const PUBLICATION: Record<CasePublication, PublicationStatus | null> = {
  [CasePublication.All]: null,
  [CasePublication.Published]: PublicationStatus.Published,
  [CasePublication.Unpublished]: PublicationStatus.Private,
};

/**
 * The cases of the library and what the reader chose to narrow them by, kept while the reader is
 * away on a case so the library comes back as it was left. The hub keeps them in step meanwhile.
 */
@Injectable({ providedIn: 'root' })
export class CaseLibraryStore {
  private readonly videoHub = inject(VideoHubService);
  private readonly http = inject(HttpClient);

  /** The categories the reader kept. Empty is the whole library. */
  readonly categories = signal<readonly string[]>([], { equal: sameCategories });
  readonly favouritesOnly = signal(false);
  /** Every favourite open in the library's first section, until a reload; not in the address. */
  readonly favouritesExpanded = signal(false);
  readonly period = signal<CasePeriod>(CasePeriod.All);
  readonly range = signal<CaseRange>(NO_RANGE);
  readonly publication = signal<CasePublication>(CasePublication.All);
  readonly sortOrder = signal<CaseSortOrder>(CaseSortOrder.NewestFirst);
  /** The text typed in the search of the header, matched by the server on the title and summary. */
  readonly searchText = signal('');
  /** Pictures or columns, as the reader last chose, kept across a reload. */
  readonly display = signal<CaseDisplay>(storedDisplay());

  readonly trimmedSearch = computed(() => this.searchText().trim());
  private readonly hasRange = computed(() => !!this.range().start || !!this.range().end);

  /** Whether categories, dates, publication or a search narrow the library; favorites-only does not. */
  readonly filtered = computed(
    () =>
      this.categories().length > 0 ||
      this.hasRange() ||
      this.publication() !== CasePublication.All ||
      this.trimmedSearch() !== '',
  );
  /** Every value applied, one per specialty, as filters combine: not the search or the sort. */
  readonly filterCount = computed(
    () =>
      this.categories().length +
      [this.favouritesOnly(), this.hasRange(), this.publication() !== CasePublication.All].filter(
        Boolean,
      ).length,
  );
  readonly hasFilters = computed(() => this.filterCount() > 0);
  /** The pressed chips of the categories row: the favorites then the kept categories. */
  readonly categoryRow = computed(() => [
    ...(this.favouritesOnly() ? [FAVOURITES_CHIP] : []),
    ...this.categories(),
  ]);
  private readonly search = computed<CaseSearch>(() => ({
    categories: this.categories(),
    favouritesOnly: this.favouritesOnly(),
    startDate: this.range().start,
    endDate: this.range().end,
    publicationStatus: PUBLICATION[this.publication()],
    sortOrder: this.sortOrder(),
    searchText: this.trimmedSearch(),
  }));
  /* The same body is the same search: a new range object with the same days must not ask again. */
  private readonly searchRequest = computed(
    () => caseSearchRequest(environment.apiUrl, this.search()),
    {
      equal: (a, b) => JSON.stringify(a.body) === JSON.stringify(b.body),
    },
  );
  /** Whether anything is chosen: the whole library is then not what is shown. */
  readonly narrowed = computed(() => Object.keys(this.searchRequest().body as object).length > 0);

  /** The whole library, read once on arrival: what is shown while nothing is chosen. */
  readonly everything: HttpResourceRef<Video[] | undefined> = httpResource(() =>
    caseSearchRequest(environment.apiUrl, {}),
  );
  /* Read only while something is chosen: nothing chosen, the whole library is already there. */
  private readonly narrowedResource: HttpResourceRef<Video[] | undefined> = httpResource(() =>
    this.narrowed() ? this.searchRequest() : undefined,
  );
  /** The search on screen: the whole library, or what the reader narrowed it to. */
  readonly resource = computed(() => (this.narrowed() ? this.narrowedResource : this.everything));
  /** The cases on screen. A new search keeps the last ones shown until it answers, so nothing blinks. */
  readonly cases = linkedSignal<Video[] | undefined, Video[] | undefined>({
    source: () => this.resource().value(),
    computation: (value, previous) => value ?? previous?.value,
  });

  /** The reader's choice as the query of the library's address; a default is null, so it is left out. */
  readonly queryParams = computed<Params>(
    () => {
      const period = this.period();
      const custom = period === CasePeriod.Custom;
      const preset = period === CasePeriod.Week || period === CasePeriod.Month;
      return {
        sort: this.sortOrder() === CaseSortOrder.NewestFirst ? null : this.sortOrder(),
        categories: this.categories().length > 0 ? this.categories().join(',') : null,
        favourites: this.favouritesOnly() ? '1' : null,
        period: preset ? period : null,
        from: custom ? this.range().start : null,
        to: custom ? this.range().end : null,
        publication: this.publication() === CasePublication.All ? null : this.publication(),
        q: this.trimmedSearch() || null,
      };
    },
    { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) },
  );

  constructor() {
    effect(() => savePreference(STORAGE_KEYS.LIBRARY_DISPLAY, this.display()));
    this.videoHub.start().subscribe();
    this.videoHub.onVideoCreated$.subscribe(() => this.reload());
    this.videoHub.onVideoUpdated$.subscribe(() => this.reload());
    // Removed locally so the card disappears at once.
    this.videoHub.onVideoDeleted$.subscribe((video) => this.remove(video.id));
  }

  reload(): void {
    this.everything.reload();
    this.narrowedResource.reload();
  }

  remove(id: string): void {
    this.patch((videos) => videos.filter((held) => held.id !== id));
  }

  /** Fills the range of a period chip, or clears it for `all`. */
  choosePeriod(period: Exclude<CasePeriod, CasePeriod.Custom>): void {
    this.period.set(period);
    this.range.set(period === CasePeriod.All ? NO_RANGE : rangeOf(period, new Date()));
  }

  /** Clears every filter counted by `filterCount`; the sort and the search stay. */
  resetFilters(): void {
    this.categories.set([]);
    this.favouritesOnly.set(false);
    this.choosePeriod(CasePeriod.All);
    this.publication.set(CasePublication.All);
  }

  /** Takes the pressed chips of the categories row back into the favorites and the categories. */
  setCategoryRow(ids: readonly string[]): void {
    this.favouritesOnly.set(ids.includes(FAVOURITES_CHIP));
    this.categories.set(ids.filter((id) => id !== FAVOURITES_CHIP));
  }

  /** Dates set by hand are a custom period; both cleared, the whole of time again. */
  setRange(range: CaseRange): void {
    this.range.set({ start: range.start, end: range.end });
    this.period.set(range.start || range.end ? CasePeriod.Custom : CasePeriod.All);
  }

  /** Whether the address holds a choice of the library, not only unrelated params. */
  hasChoice(query: ParamMap): boolean {
    return Object.keys(this.queryParams()).some((key) => query.has(key));
  }

  /** Takes back a choice read from the address; a missing or unknown value is the default. */
  restore(query: ParamMap): void {
    const categories = query.get('categories')?.split(',') ?? [];
    this.sortOrder.set(
      known(Object.values(CaseSortOrder), query.get('sort'), CaseSortOrder.NewestFirst),
    );
    this.categories.set(
      categories.filter((id) => CASE_CATEGORY_IDS.some((category) => category === id)),
    );
    this.favouritesOnly.set(query.get('favourites') === '1');
    this.publication.set(
      known(Object.values(CasePublication), query.get('publication'), CasePublication.All),
    );
    this.searchText.set(query.get('q') ?? '');
    this.restorePeriod(query);
  }

  /** Marked at once on every card and count, and taken back if the server refuses it. */
  toggleFavourite(video: Video): void {
    const flip = (favourite: boolean) =>
      this.patch((videos) =>
        videos.map((held) => (held.id === video.id ? { ...held, isFavourite: favourite } : held)),
      );
    const marked = !video.isFavourite;
    flip(marked);
    this.http
      .patch(`${environment.apiUrl}/v1/videos/${video.id}/toggle-favourite`, null)
      .subscribe({ error: () => flip(!marked) });
  }

  /* A week or a month is recomputed to today: the address names it, not its dates. */
  private restorePeriod(query: ParamMap): void {
    const period = query.get('period');
    if (period === CasePeriod.Week || period === CasePeriod.Month) {
      this.choosePeriod(period);
      return;
    }
    this.setRange({ start: parseDay(query.get('from')), end: parseDay(query.get('to')) });
  }

  /* Only a resource holding its value is updated: updating one still loading cancels its request. */
  private patch(change: (videos: Video[]) => Video[]): void {
    for (const resource of [this.everything, this.narrowedResource]) {
      if (resource.hasValue()) {
        resource.update((videos) => (videos ? change(videos) : videos));
      }
    }
  }
}
